import React from 'react';

import * as ramda from 'ramda';

import type {SelectMenuOption} from '../select-menu/select-menu';

// @ts-expect-error
import {ActionOption} from './action-option';

/**
 * Takes an array of entities and builds action menu options
 * to call, call and record, and email those particular entities.
 *
 * @param  {Array} entities   - Array of entities. Expects these objects to contain phone and email EAV arrays
 * @param  {Object} actions             - Actions to trigger when a specific option is clicked
 * @param  {Object} frontendSettings    - Instance specific settings
 * @param  {Object} capabilities        - capabilities specific settings
 * @param  {Object} clientConfig        - client config specific settings
 * @return {void}
 */
export function getActionOptionsForEntities(
    entities: any[],
    actions: {
        email: (entityId: string) => any;
        call: (arg1: {
            entityId: string;
            phoneNumber: {
                E164: string;
                numberFormatted: string;
            };
        }) => void;
    },
    frontendSettings: any = NutshellFrontendSettings,
    capabilities: any = NutCapabilities,
    clientConfig: any = NutClientConfig
) {
    // We'll build up these options for all related entities
    const options = {
        call: {
            label: 'Call',
            submenu: [],
        },
        email: {
            label: 'Email',
            submenu: [],
        },
    } as const;

    // Loop through each entity and conditionally add submenu items based
    // on feature flags and properties on the entity
    entities.forEach((entity) => {
        if (
            frontendSettings.phonecaller_type === 'clicktocall' &&
            capabilities.clickToCall.isAvailable &&
            entity.phones
        ) {
            // @ts-expect-error
            const primaryPhone = entity.phones.find((phone) => phone.isPrimary);
            if (primaryPhone) {
                // @ts-expect-error
                options.call.submenu.push({
                    key: entity.id,
                    label: (
                        <ActionOption entity={entity} value={primaryPhone.value.numberFormatted} />
                    ),
                    handleSelect: () =>
                        actions.call({
                            entityId: entity.id,
                            phoneNumber: primaryPhone.value,
                        }),
                });
            }
        }

        if (entity.emails) {
            // @ts-expect-error
            const primaryEmail = entity.emails.find((email) => email.isPrimary);
            if (primaryEmail) {
                // @ts-expect-error
                options.email.submenu.push({
                    key: entity.id,
                    label: <ActionOption entity={entity} value={primaryEmail.value} />,
                    handleSelect: () =>
                        handleEmailEntity({
                            entityId: entity.id,
                            primaryEmailAddress: primaryEmail.value,
                            onSelectEmailOption: actions.email,
                            sendFromNutshell: clientConfig.sendFromNutshellEnabled,
                        }),
                });
            }
        }
    });

    return (
        ramda
            .values(options)
            // @ts-expect-error
            .filter((option: SelectMenuOption) => option.submenu && option.submenu.length)
    );
}

type HandleEmailEntityOptions = {
    entityId: string;
    primaryEmailAddress: string;
    onSelectEmailOption: (entityId: string) => any;
    sendFromNutshell: boolean;
};

// Intercept callback in case we should just pop a mailto
// window instead of actually trying to show our email modal
function handleEmailEntity(options: HandleEmailEntityOptions) {
    if (options.sendFromNutshell) {
        options.onSelectEmailOption(options.entityId);
    } else {
        window.open(`mailto:${options.primaryEmailAddress}?bcc=bcc@nutshell.com`);
    }
}
